import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import {
  MerchantRoutes,
  MerchantRoutesContext,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  ClaimSystemView,
  ReturnSystemViewsNoCreate,
} from "@redotech/redo-return-operations-common/views/default-return-views";
import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import type { JSXElementConstructor } from "react";
import { recordViewHref } from "../record-links/record-links";
import {
  getReturnListViewLabel,
  isReturnListSystemViewIdentity,
} from "../returns-list-labels";
import { recordViewsQueryKey } from "./record-views-query";
import { useRecordFilterCounts } from "./use-record-filter-counts";
import {
  recordViewCountsQueryKey,
  RecordViewType,
} from "./use-record-view-counts";

type RecordViewT = ReturnType<typeof useTranslation>["t"];

export type RecordViewRoutes = Pick<MerchantRoutes, "returnsBaseUrl">;

/** A view a type offers besides All and saved views, defined by its filters. */
export interface BuiltInRecordView {
  id: string;
  label(t: RecordViewT): string;
  filters: AdvancedFilterData[];
  href(routes: RecordViewRoutes): string;
  /** Red when above zero, for views that ask the merchant to act. */
  urgent: boolean;
}

/** One record type's views: what the catalog needs to list and count them. */
export interface RecordViewCatalogSource {
  type: string;
  recordType: ReturnTypeEnum;
  Icon: JSXElementConstructor<object>;
  copy(t: RecordViewT): { recordsLabel: string; allLabel: string };
  views: {
    viewType: RecordViewType;
    /** Whether saved views show their counts. */
    counted: boolean;
  };
  quickViews: readonly BuiltInRecordView[];
  allHref(routes: RecordViewRoutes): string;
}

/** A built-in or saved view of any type, linked to its table. */
export interface RecordViewLink {
  /** `${source type}:${view id}`, stable across visits. */
  key: string;
  Icon: JSXElementConstructor<object>;
  label: string;
  count?: number;
  /** Red when above zero. */
  urgent: boolean;
  href: string;
  /** A built-in view's filters; unset for All and saved views. */
  filters?: AdvancedFilterData[];
  /** All and the built-ins, as opposed to the team's own saved views. */
  builtIn: boolean;
}

export interface RecordViewGroup {
  id: string;
  label: string;
  views: RecordViewLink[];
}

/** Pages served by a view route that aren't a view to list. */
const NOT_LISTED_VIEWS = new Set<string>([
  ...ReturnSystemViewsNoCreate,
  ClaimSystemView.MANAGED,
]);

/** Every view of each source, grouped by source: All, built-ins, then the team's own views; system views stay out. */
export function useRecordViewCatalog(
  sources: readonly RecordViewCatalogSource[],
): RecordViewGroup[] {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  // Same keys and data as `useRecordViews`, so the side nav's views are reused.
  const viewLists = useQueries({
    queries: sources.map((source) => ({
      queryKey: recordViewsQueryKey(team._id, source.recordType),
      queryFn: () =>
        rpcClient.getReturnViewsAndFolders({ type: source.recordType }),
      placeholderData: keepPreviousData,
    })),
  });
  const savedViews = sources.map((source, index) => {
    const builtInIds = new Set(source.quickViews.map((view) => view.id));
    return (viewLists[index]?.data?.views ?? []).filter(
      (view) =>
        !NOT_LISTED_VIEWS.has(view.name) &&
        !builtInIds.has(view.name) &&
        !isReturnListSystemViewIdentity(view.name),
    );
  });

  const savedCounts = useQueries({
    queries: sources.map((source, index) => {
      const viewIds = (savedViews[index] ?? [])
        .flatMap((view) => (view._id ? [view._id] : []))
        .sort();
      return {
        queryKey: [
          ...recordViewCountsQueryKey(team._id, source.views.viewType),
          { viewIds, includeAll: true },
        ],
        queryFn: () =>
          rpcClient.getReturnViewNotificationCounts({
            viewIds,
            type: source.views.viewType,
            includeAll: true,
          }),
        enabled: source.views.counted && viewIds.length > 0,
        placeholderData: keepPreviousData,
      };
    }),
  });
  const { counts: builtInCounts } = useRecordFilterCounts(
    sources.flatMap((source) =>
      source.quickViews.map((view) => ({
        key: `${source.type}:${view.id}`,
        type: source.recordType,
        filters: view.filters,
      })),
    ),
  );

  return sources.map((source, index) => {
    const copy = source.copy(t);
    const counts = savedCounts[index]?.data?.counts ?? {};
    return {
      id: source.type,
      label: copy.recordsLabel,
      views: [
        {
          key: `${source.type}:all`,
          Icon: source.Icon,
          label: copy.allLabel,
          urgent: false,
          href: source.allHref(routes),
          builtIn: true,
        },
        ...source.quickViews.map((view) => ({
          key: `${source.type}:${view.id}`,
          Icon: source.Icon,
          label: view.label(t),
          count: builtInCounts[`${source.type}:${view.id}`],
          urgent: view.urgent,
          href: view.href(routes),
          filters: view.filters,
          builtIn: true,
        })),
        ...(savedViews[index] ?? []).map((view) => ({
          key: `${source.type}:view:${view._id ?? view.name}`,
          Icon: source.Icon,
          label: getReturnListViewLabel(view.name),
          count:
            source.views.counted && view._id ? counts[view._id] : undefined,
          urgent: false,
          href: recordViewHref(
            routes.returnsBaseUrl,
            source.recordType,
            view.name,
          ),
          builtIn: false,
        })),
      ],
    };
  });
}
